import React, { useState } from 'react';
import { useSystemStatus } from '../hooks/useSystemStatus';

interface SystemStatusIndicatorProps {
  showDetails?: boolean;
  className?: string;
}

export default function SystemStatusIndicator({
  showDetails = false,
  className = ''
}: SystemStatusIndicatorProps): React.ReactElement {
  const { status, isChecking, checkHealth } = useSystemStatus({
    checkInterval: 120000, // Check every 2 minutes
    autoStart: true
  });
  const [showTooltip, setShowTooltip] = useState(false);

  const getCircleStatusColor = () => {
    if (isChecking) return 'bg-gray-100';

    switch (status.status) {
      case 'online':
        return 'bg-green-100';
      case 'degraded':
        return 'bg-yellow-100';
      case 'offline':
        return 'bg-red-100';
      default:
        return 'bg-gray-100';
    }
  };

  const getSmallCircleStatusColor = () => {
    if (isChecking) return 'bg-gray-400';

    switch (status.status) {
      case 'online':
        return 'bg-green-400';
      case 'degraded':
        return 'bg-yellow-400';
      case 'offline':
        return 'bg-red-400';
      default:
        return 'bg-gray-400';
    }
  };

  const getStatusText = () => {
    if (isChecking) return 'Verificando...';

    switch (status.status) {
      case 'online':
        return 'Sistema Online';
      case 'degraded':
        return 'Sistema Instável';
      case 'offline':
        return 'Sistema Offline';
      default:
        return 'Status Desconhecido';
    }
  };

  const formatLatency = (latency?: number) => {
    return latency ? `${latency}ms` : 'N/A';
  };

  const formatLastChecked = (date: Date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return 'agora mesmo';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes}min atrás`;
    } else {
      return date.toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  return (
    <div className={`relative ${className}`}>
      <div
        className="flex items-center cursor-pointer"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onClick={checkHealth}
      >
        <div
          className={`flex items-center justify-center w-8 h-8 ${getCircleStatusColor()} rounded-full`}
        >
          <div
            className={`w-2 h-2 rounded-full transition-colors ${getSmallCircleStatusColor()} ${isChecking ? 'animate-pulse' : ''}`}
          ></div>
        </div>
        <div className="ml-3">
          <p className="text-xs text-gray-500">{getStatusText()}</p>
          {showDetails && (
            <p className="text-xs text-gray-400">
              Atualizado {formatLastChecked(status.lastChecked)}
            </p>
          )}
        </div>
      </div>

      {showTooltip && (
        <div className="absolute bottom-full left-0 mb-2 w-64 p-3 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="font-medium text-sm">Status do Sistema</span>
              <span
                className={`px-2 py-1 rounded-full text-xs font-medium ${
                  status.status === 'online'
                    ? 'bg-green-100 text-green-800'
                    : status.status === 'degraded'
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-red-100 text-red-800'
                }`}
              >
                {status.status === 'online'
                  ? 'Online'
                  : status.status === 'degraded'
                    ? 'Instável'
                    : 'Offline'}
              </span>
            </div>

            <hr />

            <div className="space-y-1">
              <div className="flex justify-between text-xs">
                <span>Optosoft:</span>
                <span
                  className={`${
                    status.supabase.status === 'operational'
                      ? 'text-green-600'
                      : status.supabase.status === 'degraded'
                        ? 'text-yellow-600'
                        : 'text-red-600'
                  }`}
                >
                  {status.supabase.status === 'operational'
                    ? 'Operacional'
                    : status.supabase.status === 'degraded'
                      ? 'Instável'
                      : 'Fora do ar'}
                  {status.supabase.latency && ` (${formatLatency(status.supabase.latency)})`}
                </span>
              </div>

              <div className="flex justify-between text-xs">
                <span>Internet:</span>
                <span
                  className={`${
                    status.internet.status === 'operational'
                      ? 'text-green-600'
                      : status.internet.status === 'degraded'
                        ? 'text-yellow-600'
                        : 'text-red-600'
                  }`}
                >
                  {status.internet.status === 'operational'
                    ? 'Conectado'
                    : status.internet.status === 'degraded'
                      ? 'Lento'
                      : 'Desconectado'}
                  {status.internet.latency && ` (${formatLatency(status.internet.latency)})`}
                </span>
              </div>
            </div>

            <hr />

            <div className="text-xs text-gray-500">
              Última verificação: {formatLastChecked(status.lastChecked)}
            </div>

            {(status.supabase.error || status.internet.error) && (
              <div className="text-xs text-red-600 mt-2">
                {status.supabase.error && <div>DB: {status.supabase.error}</div>}
                {status.internet.error && <div>Net: {status.internet.error}</div>}
              </div>
            )}

            <div className="text-xs text-gray-400 italic">Clique para verificar novamente</div>
          </div>
        </div>
      )}
    </div>
  );
}
